import { useEffect, MutableRefObject } from 'react'
import { useHistory } from 'react-router-dom'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { useAppSelector } from '@redux/hooks'
import type { EditListProps, QueuedResolution, LiveblocksEditListProps } from './types'

const isLiveblocksProps = (props: EditListProps): props is LiveblocksEditListProps =>
  'threads' in props

export const useLiveblocksHooks = (
  props: EditListProps,
  setters: {
    setReadOnly: React.Dispatch<React.SetStateAction<boolean>>
    setShouldRefetch: React.Dispatch<React.SetStateAction<boolean>>
  },
) => {
  const {
    threads = [],
    activeCommentTarget = null,
    setActiveCommentTarget = () => void {},
    hasPendingComments = false,
    checkPendingStatus = () => false,
    updateLocalThreadStatus = () => void {},
    queueThreadResolution = () => void {},
    handlePendingThreadResolutions,
    queuedResolutionsRef = { current: new Map() } as MutableRefObject<
      Map<string, QueuedResolution> | undefined
    >,
    createThread = () => void {},
    editThreadMetadata = () => void {},
    selectedCommentType,
    setSelectedCommentType = () => void {},
    threadsToDisplayInSidePanel = [],
    hasUnresolvedGeneralComments = false,
    handleRefetch = () => void {},
    reviewModeHandlers = {},
  } = isLiveblocksProps(props) ? props : {}
  const { setReadOnly, setShouldRefetch } = setters

  const history = useHistory()

  const {
    isOwner = false,
    setLock = () => void {},
    releaseLock = () => void {},
    isLocked = false,
  } = reviewModeHandlers

  const [ENABLE_LIVEBLOCKS = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.ENABLE_LIVEBLOCKS]),
  )

  const permissions = useAppSelector(state => state.Auth.permissions)
  const isApproverMode = !ENABLE_LIVEBLOCKS || Boolean(permissions.approve_suggestions)

  const tableWrapper = document.getElementById('table-wrapper')
  const sidePanel = document.getElementById('side-panel')

  const handleReviewModeEvent = () => {
    if (isApproverMode && ENABLE_LIVEBLOCKS) {
      setLock()
    }
  }

  // TODO: Explore a different approach to managing elements for the Review action
  useEffect(() => {
    if (!tableWrapper) return

    tableWrapper.addEventListener('mousemove', handleReviewModeEvent)

    return () => {
      tableWrapper.removeEventListener('mousemove', handleReviewModeEvent)
    }
  }, [tableWrapper])

  useEffect(() => {
    if (!sidePanel) return

    sidePanel.addEventListener('mousemove', handleReviewModeEvent)

    return () => {
      sidePanel.removeEventListener('mousemove', handleReviewModeEvent)
    }
  }, [sidePanel])

  useEffect(() => {
    const unlisten = history.listen(() => {
      if (isOwner) {
        releaseLock()
      }
    })

    return () => {
      unlisten()
    }
  }, [history, isOwner, releaseLock])

  useEffect(() => {
    handleRefetch(() => {
      setShouldRefetch(true)
    })
  }, [handleRefetch])

  useEffect(() => {
    if (!isApproverMode) {
      setReadOnly(true)
    }
  }, [isApproverMode])

  return {
    isLiveblocksEnabled: ENABLE_LIVEBLOCKS,
    isApproverMode,
    isOwner,
    isLocked,
    threads,
    activeCommentTarget,
    setActiveCommentTarget,
    hasPendingComments,
    checkPendingStatus,
    updateLocalThreadStatus,
    queueThreadResolution,
    handlePendingThreadResolutions,
    queuedResolutionsRef,
    createThread,
    editThreadMetadata,
    selectedCommentType,
    setSelectedCommentType,
    threadsToDisplayInSidePanel,
    hasUnresolvedGeneralComments,
  }
}
